import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { StaffViewButton } from '../components/StaffViewButton'
import { MoneyDialog, type MoneyAction } from './MoneyDialog'
import { getHistory, getMember, type HistoryItem, type HistoryKind, type MemberRecord, type TitleCard } from './api'
import { EditDetails } from './EditDetails'
import { EndDate } from './EndDate'
import { QuickActions } from './QuickActions'
import { deskHref, memberHref, paymentHref, printHref } from './links'
import { canPrintReceipt, canResend, cardActions, dollars, fmtDay, fmtMonth, groupByMonth } from './member'
import { HelpLink } from './HelpLink'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-[36px] items-center rounded-lg border border-ink-400 px-3 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const card = 'flex flex-col gap-2 rounded-xl border border-ink-500 bg-ink-700 p-4'
const TABS: { kind: HistoryKind; label: string }[] = [
  { kind: 'all', label: 'Everything' },
  { kind: 'money', label: 'Payments' },
  { kind: 'comms', label: 'Messages' },
  { kind: 'change', label: 'Account changes' },
]
const TONE: Record<HistoryItem['tone'], string> = { ok: 'text-green-500', warn: 'text-amber-glow', bad: 'text-red-500', muted: 'text-paper/60' }
const STATE_WORD = { active: 'Active', lapsed: 'Lapsed', cancelled: 'Cancelled', never: 'Never a member' } as const

function Tag({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-ink-500 px-2.5 py-0.5 text-[12px] text-paper/70">{children}</span>
}

function TitleCardView({ t, memberId, adjusting, onEndDate }: {
  t: TitleCard; memberId: number; adjusting: boolean; onEndDate: () => void
}) {
  const actions = cardActions(t)
  const never = t.state === 'never'
  return (
    <div className={`${card} ${never ? 'border-dashed' : ''}`}>
      <span className="text-[12px] font-bold uppercase tracking-wider text-paper/60">
        {STATE_WORD[t.state]}{t.state === 'lapsed' || t.state === 'cancelled' ? (t.end_date ? ` ${fmtMonth(t.end_date)}` : '') : ''}
      </span>
      <strong className={`text-[16px] ${never ? 'text-paper/70' : ''}`}>{t.name}</strong>
      {!never && (
        <span className="leading-relaxed text-paper/70">
          {t.end_date && <>{t.state === 'active' ? 'Ends' : 'Ended'} <strong className="text-paper">{fmtMonth(t.end_date)}</strong><br /></>}
          {t.since && <>Member since {t.since}<br /></>}
          {t.paid_by && <>Gift from {t.paid_by}<br /></>}
          {t.last_payment?.method && <>Paid by {t.last_payment.method}<br /></>}
          {t.state === 'active' && (t.auto_renew ? 'Renews automatically.' : 'No auto-renew.')}
        </span>
      )}
      {t.sc_owned && <span className="text-[13px] text-paper/70">Managed in SimpleCirc</span>}
      {(actions.includes('pay') || actions.includes('end-date')) && (
        <div className="flex flex-wrap gap-1.5">
          {actions.includes('end-date') && t.subscription_id !== null && (
            <button type="button" className={btn} aria-expanded={adjusting} onClick={onEndDate}>Change end date</button>
          )}
          {actions.includes('pay') && <Link className={btn} to={paymentHref(memberId, { title: t.slug })}>Record a payment</Link>}
        </div>
      )}
    </div>
  )
}

function HistoryRow({ e, memberId, onAct }: {
  e: HistoryItem; memberId: number; onAct: (a: MoneyAction, item: HistoryItem) => void
}) {
  return (
    <div className="flex items-start gap-3 border-t border-ink-600 py-3">
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <strong>{e.what}</strong>
          {e.title_abbr && <Tag>{e.title_abbr}</Tag>}
        </span>
        {e.detail && <span className="leading-snug text-paper/70">{e.detail}</span>}
        <span className="text-[12px]">
          {e.status && <span className={TONE[e.tone]}>{e.status}</span>}
          {e.forwarded_message_id && e.forwarded_publication && (
            <> · <Link to={`/support/${e.forwarded_publication}?c=contact-${e.forwarded_message_id}`} className="underline underline-offset-2">Open in Customer Support</Link></>
          )}
          {canPrintReceipt(e) && e.order_key && (
            <> · <a href={`${printHref(memberId, 'receipt')}?order_key=${encodeURIComponent(e.order_key)}`} target="_blank" rel="noopener" className="underline underline-offset-2">Print receipt</a></>
          )}
          <span className="text-paper/60"> · {fmtDay(e.at)}{e.by ? ` · ${e.by}` : ''}</span>
        </span>
        {(e.can_void || e.can_refund) && e.order_key && (
          <span className="flex flex-wrap items-center gap-2 pt-1">
            {e.can_void && <button type="button" className={btn} onClick={() => onAct('void', e)}>Void</button>}
            {e.can_refund && <button type="button" className={btn} onClick={() => onAct('refund', e)}>Refund</button>}
          </span>
        )}
      </span>
      {e.amount_cents !== null && <span className="whitespace-nowrap font-mono text-[14px]">{dollars(e.amount_cents)}</span>}
    </div>
  )
}

function HistoryList({ id, kind, onAct }: { id: number; kind: HistoryKind; onAct: (a: MoneyAction, item: HistoryItem) => void }) {
  const [items, setItems] = useState<HistoryItem[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  function take(before: string | null) {
    getHistory(id, kind, before)
      .then((page) => { setItems((cur) => [...cur, ...page.items]); setNext(page.next_before) })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }
  function older(before: string) {
    setLoading(true)
    setError(false)
    take(before)
  }
  // Mounted fresh (keyed) for each member and tab, so state starts empty and loading.
  useEffect(() => { take(null) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {groupByMonth(items).map((g) => (
        <div key={g.month}>
          <div className="pt-4 text-[12px] font-bold uppercase tracking-wider text-paper/60">{g.month}</div>
          {g.items.map((e, i) => <HistoryRow key={`${e.at}-${e.order_key ?? ''}-${i}`} e={e} memberId={id} onAct={onAct} />)}
        </div>
      ))}
      {!loading && !error && items.length === 0 && <p className="py-6 text-paper/70">Nothing here yet.</p>}
      {loading && <p role="status" className="py-4 text-paper/70">Loading…</p>}
      {error && <p role="alert" className="py-4 text-red-500">Could not load history. Reload the page to try again.</p>}
      {next && !loading && (
        <div className="mt-1 border-t border-ink-600 pt-4">
          <button type="button" className={btn} onClick={() => older(next)}>Show older</button>
        </div>
      )}
    </>
  )
}

function History({ id, onAct }: { id: number; onAct: (a: MoneyAction, item: HistoryItem) => void }) {
  const [kind, setKind] = useState<HistoryKind>('all')
  return (
    <section aria-labelledby="hist-h" className="min-w-0 flex-[999_1_520px] rounded-xl border border-ink-500 bg-ink-700 px-5 pb-4 pt-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="hist-h" className="sr-only">History</h2>
        <div role="tablist" aria-label="History" className="flex flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.kind}
              type="button"
              role="tab"
              aria-selected={kind === t.kind}
              onClick={() => setKind(t.kind)}
              className={`border-b-2 px-3 py-3 font-semibold ${focus} ${kind === t.kind ? 'border-amber-glow text-paper' : 'border-transparent text-paper/60 hover:text-paper'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-paper/60">All titles · newest first</span>
      </div>
      <HistoryList key={`${id}-${kind}`} id={id} kind={kind} onAct={onAct} />
    </section>
  )
}

function Header({ m, onEdit }: { m: MemberRecord; onEdit: () => void }) {
  const a = m.address
  const street = [a.address_1, a.address_2].filter(Boolean).join(', ')
  const cityLine = [a.city, [a.state, a.zipcode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  const address = [street, cityLine].filter(Boolean).join(', ')
  const tags = m.member_since ? [...m.tags, `Member since ${m.member_since}`] : m.tags
  return (
    <header className="flex flex-wrap items-end justify-between gap-5">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="font-serif text-4xl font-medium tracking-tight">{m.name}</h1>
          {m.account_number && <span className="font-mono text-[14px] text-paper/60">#{m.account_number}</span>}
          <button type="button" onClick={onEdit} className={`${btn} self-center`}>Edit details</button>
        </div>
        {m.company && <span className="text-paper/70">{m.company}</span>}
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-paper/70">
          {address && <span>{address}</span>}
          {m.email && <span>{m.email}</span>}
          {m.phone && <span>{m.phone}</span>}
        </div>
        {tags.length > 0 && <div className="flex flex-wrap gap-1.5">{tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>}
      </div>
      <StaffViewButton subscriberId={m.id} stacked />
    </header>
  )
}

export function MemberPage() {
  const id = Number(useParams().id)
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [adjusting, setAdjusting] = useState<string | null>(null)
  const [historyKey, setHistoryKey] = useState(0)
  const [money, setMoney] = useState<{ action: MoneyAction; item: HistoryItem } | null>(null)
  const [moneyDone, setMoneyDone] = useState<string | null>(null)
  const { data: m, isError, isLoading } = useQuery({ queryKey: ['desk-member', id], queryFn: () => getMember(id), enabled: Number.isFinite(id) })

  if (isLoading) return <main className="p-6 text-paper/70">Loading…</main>
  if (isError || !m) {
    return (
      <main className="flex flex-col gap-3 p-6">
        <p role="alert">We could not find that member.</p>
        <Link to={deskHref('search')} className="underline">Find a member</Link>
      </main>
    )
  }
  const adjustCard = m.titles.find((t) => t.slug === adjusting && t.subscription_id !== null) ?? null
  return (
    <main className="flex min-w-0 flex-1 flex-col gap-7 px-4 pb-16 pt-8 md:px-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={deskHref('search')} className="text-[13px] underline-offset-2 hover:underline">← Find a member</Link>
        <HelpLink page="member" />
      </div>
      <Header m={m} onEdit={() => setEditing(true)} />
      {moneyDone && <p role="status" className="m-0 rounded-lg bg-ink-700 px-4 py-2 text-green-500">{moneyDone}</p>}
      {money && (
        <MoneyDialog
          action={money.action}
          item={money.item}
          onClose={() => setMoney(null)}
          onDone={(msg) => {
            setMoney(null)
            setMoneyDone(msg)
            setHistoryKey((k) => k + 1)
            void qc.invalidateQueries({ queryKey: ['desk-member', id] })
          }}
        />
      )}
      <QuickActions m={m} onLogged={() => setHistoryKey((k) => k + 1)} />
      {editing && (
        <EditDetails
          m={m}
          onCancel={() => setEditing(false)}
          onSaved={(fresh) => {
            qc.setQueryData(['desk-member', id], fresh)
            setHistoryKey((k) => k + 1)
            setEditing(false)
          }}
        />
      )}

      <section aria-labelledby="titles-h" className="flex flex-col gap-3">
        <h2 id="titles-h" className="text-[12px] font-bold uppercase tracking-widest text-paper/60">Titles</h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
          {m.titles.map((t) => (
            <TitleCardView key={t.slug} t={t} memberId={m.id} adjusting={adjusting === t.slug}
              onEndDate={() => setAdjusting((cur) => (cur === t.slug ? null : t.slug))} />
          ))}
        </div>
        {adjustCard && (
          <EndDate
            key={adjustCard.slug}
            memberId={m.id}
            card={adjustCard}
            canEmail={canResend(m)}
            onCancel={() => setAdjusting(null)}
            onSaved={() => {
              setAdjusting(null)
              setHistoryKey((k) => k + 1)
              void qc.invalidateQueries({ queryKey: ['desk-member', id] })
            }}
          />
        )}
      </section>

      <div className="flex flex-wrap items-start gap-7">
        <History key={historyKey} id={m.id} onAct={(action, item) => { setMoneyDone(null); setMoney({ action, item }) }} />
        <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-4">
          <div className={card}>
            <h3 className="font-serif text-[18px]">Gifts paid for</h3>
            {m.gifts_paid_for.length === 0 && <span className="text-paper/70">None.</span>}
            {m.gifts_paid_for.map((g) => (
              <Link key={`${g.subscriber_id}-${g.slug}`} to={memberHref(g.subscriber_id)} className="flex flex-col">
                <strong>{g.name}</strong>
                <span className="text-[13px] text-paper/70">{g.abbr} gift · {g.state === 'active' ? 'through' : 'ended'} {fmtMonth(g.end_date)}</span>
              </Link>
            ))}
          </div>
          <div className={card}>
            <h3 className="font-serif text-[18px]">Money in, all titles</h3>
            <div className="grid grid-cols-2 gap-2.5">
              <span className="flex flex-col"><span className="font-mono text-[20px]">{dollars(m.totals.paid_cents)}</span><span className="text-[12px] text-paper/60">Lifetime paid</span></span>
              <span className="flex flex-col"><span className="font-mono text-[20px]">{dollars(m.totals.refunded_cents)}</span><span className="text-[12px] text-paper/60">Refunded</span></span>
            </div>
          </div>
          <div className={card}>
            <h3 className="font-serif text-[18px]">Staff notes</h3>
            <p className="m-0 whitespace-pre-wrap leading-relaxed text-paper/70">{m.notes || 'No notes.'}</p>
          </div>
        </aside>
      </div>
    </main>
  )
}
